import { forwardRef, useEffect, useId, useRef, type ReactNode, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Textarea>. Layout,
// autoResize/maxRows sizing, and CSS ported letter-for-letter from the
// vendor's compiled bundle and its Textarea CSS — new .rc-textarea class
// names. No validation engine to drop (the vendor never had one for this
// component, unlike Input's rule system).
export type TextareaResize = "none" | "vertical" | "horizontal" | "both";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  resize?: TextareaResize;
  autoResize?: boolean;
  maxRows?: number;
  maxLength?: number;
}

function setHeightToFitContent(el: HTMLTextAreaElement, maxRows: number) {
  el.style.height = "auto";
  const styles = window.getComputedStyle(el);
  const lineHeight = parseFloat(styles.lineHeight) || 20;
  const paddingY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
  const borderY = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
  const maxHeight = lineHeight * maxRows + paddingY + borderY;
  el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    help,
    error,
    required,
    resize = "vertical",
    autoResize = false,
    maxRows = 10,
    maxLength,
    rows = 4,
    className,
    id: providedId,
    value,
    defaultValue,
    onChange,
    ...rest
  },
  ref
) {
  const fallbackId = useId();
  const fieldId = providedId ?? fallbackId;
  const helpId = help ? `${fieldId}-help` : undefined;
  const errorId = error ? `${fieldId}-err` : undefined;
  const countId = maxLength != null ? `${fieldId}-count` : undefined;
  const describedBy = [helpId, errorId, countId].filter(Boolean).join(" ") || undefined;

  const internalRef = useRef<HTMLTextAreaElement | null>(null);
  function setRef(node: HTMLTextAreaElement | null) {
    internalRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
  }

  useEffect(() => {
    if (autoResize && internalRef.current) {
      setHeightToFitContent(internalRef.current, maxRows);
    }
  }, [autoResize, maxRows, value]);

  const currentLength = typeof value === "string" ? value.length : typeof defaultValue === "string" ? defaultValue.length : 0;
  const over = maxLength != null && currentLength > maxLength;

  return (
    <div className={cn("rc-textarea", error && "rc-textarea--error", className)}>
      <label htmlFor={fieldId} className="rc-textarea__label">
        {label}
        {required ? (
          <span className="rc-textarea__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <textarea
        ref={setRef}
        id={fieldId}
        rows={rows}
        required={required}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn("rc-textarea__field", autoResize ? "rc-textarea__field--autoresize" : `rc-textarea__field--resize-${resize}`)}
        value={value}
        defaultValue={defaultValue}
        onChange={(e) => {
          if (autoResize && internalRef.current) {
            setHeightToFitContent(internalRef.current, maxRows);
          }
          onChange?.(e);
        }}
        {...rest}
      />
      {help ? (
        <span id={helpId} className="rc-textarea__help">
          {help}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="rc-textarea__error" role="alert">
          {error}
        </span>
      ) : null}
      {maxLength != null ? (
        <span id={countId} className={cn("rc-textarea__count", over && "rc-textarea__count--over")}>
          {currentLength} / {maxLength}
        </span>
      ) : null}
    </div>
  );
});
